import { Fragment } from 'react';
import type { CycleStatus } from '../../../api/types';

const STEPS: CycleStatus[] = ['diagnosed', 'assigned', 'retested', 'closed'];

export const fmtDelta = (v: number, digits = 1) => `${v > 0 ? '+' : ''}${v.toFixed(digits)}`;

/** Four dots, one per stage; every stage up to the current one is filled. */
export function StatusTracker({ status }: { status: CycleStatus }) {
  const at = STEPS.indexOf(status);
  return (
    <span className="inline-flex items-center gap-2">
      <span role="img" aria-label={`Step ${at + 1} of ${STEPS.length}`} className="inline-flex items-center gap-1">
        {STEPS.map((step, i) => (
          <Fragment key={step}>
            {i > 0 && <span aria-hidden className={`h-0.5 w-2 ${i <= at ? 'bg-slate' : 'bg-track'}`} />}
            <span aria-hidden title={step}
              className={`h-2.5 w-2.5 rounded-full ${i < at ? 'bg-slate' : i === at ? 'bg-accent ring-2 ring-accent/30' : 'bg-track'}`} />
          </Fragment>
        ))}
      </span>
      <span className="text-xs font-semibold capitalize text-ink">{status}</span>
    </span>
  );
}

/** Design SD `.steps`: four labelled stages joined by a rule; done is slate, the current one accent (a closed cycle is all done). */
export function LabelledSteps({ status, stepWidth = 66 }: { status: CycleStatus; stepWidth?: 66 | 70 }) {
  const at = STEPS.indexOf(status);
  return (
    <div role="img" aria-label={`Status: ${status}, step ${at + 1} of ${STEPS.length}`} className="flex flex-none items-start">
      {STEPS.map((step, k) => {
        const done = k < at || (k === at && status === 'closed');
        const cur = k === at && status !== 'closed';
        return (
          <span key={step} aria-hidden
            className={`relative flex ${stepWidth === 70 ? 'w-[70px]' : 'w-[66px]'} flex-col items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.04em] before:absolute before:right-1/2 before:top-[7px] before:h-0.5 before:w-full first:before:hidden ${
              cur ? 'text-ink' : 'text-muted'} ${done || cur ? 'before:bg-slate' : 'before:bg-line'}`}>
            <span className={`relative z-[1] h-3 w-3 rounded-full border-2 border-surface ${
              cur ? 'bg-accent ring-1 ring-accent' : done ? 'bg-slate ring-1 ring-slate' : 'bg-line ring-1 ring-line-strong'}`} />
            {step}
          </span>
        );
      })}
    </div>
  );
}
